/**
 * The shell — everything `AA.DAT` says about one round of archie, and the test
 * `aa.c` puts it to.
 *
 * Split out of `flak.ts` so the battery model there stays readable: this file
 * is constants and one pure function over them. The decode, the evidence, and
 * the correction that put `burstLethality` here rather than a die roll in
 * `stepFlak`, are all in
 * [AD-010](../../../../docs/engineering/flak-design.md) — §3 and §9.
 */

import { HIT_TARGETS, isComponent } from "./hit-allocation.ts";

/**
 * One world unit in the original is 1/8 foot (`re-sessions/calib/NOTES.md` §2,
 * cross-checked three ways). Every distance in `AA.DAT` is in those units, so
 * this is the only conversion in the file.
 */
export const METRES_PER_UNIT = 0.3048 / 8;

/** The 54 bytes of `AA.DAT`, at their file offsets, exactly as `aa.c` reads them. */
export const AA_DAT_FIELDS = {
  /** +0x00 — how many shots a slot may count before it stops improving. */
  trackingCap: 5,
  /** +0x02 / +0x04 — reload is `base + (rand & mask)` timer units. */
  reloadBaseUnits: 30,
  reloadJitterMask: 31,
  /** +0x0c — the shift applied to a battery's own altitude to get the shell's fuse. */
  fuseAltitudeShift: 7,
  /** +0x10 — engagement radius of a `heavy` battery, world units. */
  heavyRangeUnits: 50000,
  /** +0x14 — ditto, `light`. */
  lightRangeUnits: 20000,
  /** +0x18 — no battery engages anything above this altitude. */
  ceilingUnits: 36000,
  /** +0x1c — nor below this one. */
  floorUnits: 5000,
  /** +0x22 — scatter half-width by tracking level, world units. */
  scatterUnits: [1500, 700, 200],
  /** +0x28 — systematic aim bias by tracking level, world units. */
  aimBiasUnits: [4000, 2000, 200],
  /**
   * +0x2e — the burst's effect radius in each of its four animation frames,
   * world units. `aa.c` indexes this table with the burst's own frame number
   * and compares the entry against the *3D distance* from the burst to the
   * aeroplane; that comparison is what decides whether a shell hurts. See
   * `burstLethality`.
   */
  burstRadiiUnits: [50, 175, 50, 10],
} as const;

/**
 * `+4` in `aa.c`'s fuse expression, which is `(battery.z >> 7) + 4`. Every
 * battery the original places sits at `z = 0` (all 18 in a live balloon-busting
 * mission), so the shift term is always zero and the fuse is always 4 — but the
 * expression is kept because it is what the game computes, and a battery placed
 * on high ground would get a longer one.
 */
export const FUSE_BASE_UNITS = 4;

/**
 * Seconds per `AA.DAT` timer unit, MEASURED-DOSBOX. `aa.c` and `smoke.c` share
 * one countdown helper draining a global frame delta, so the unit is the same
 * in both files; five slot timers sampled across 112 BIOS ticks pin the drain
 * to 28.2–29.0 units (the bracket closes because one slot had just reloaded at
 * the minimum 30 and another at the maximum 61). See AD-010 §4.
 */
export const FLAK_TIMER_UNIT_SECONDS = 0.2151;

export const FLAK_CLASSES = ["light", "heavy"] as const;

export type FlakClass = (typeof FLAK_CLASSES)[number];

/** Five, from `aa.c`'s own loop bound — not a port choice. */
export const FLAK_SLOTS_PER_BATTERY = 5;

export const FLAK_ENVELOPE = {
  ceilingM: AA_DAT_FIELDS.ceilingUnits * METRES_PER_UNIT,
  floorM: AA_DAT_FIELDS.floorUnits * METRES_PER_UNIT,
  rangeM: {
    light: AA_DAT_FIELDS.lightRangeUnits * METRES_PER_UNIT,
    heavy: AA_DAT_FIELDS.heavyRangeUnits * METRES_PER_UNIT,
  },
} as const;

/** Aim bias and scatter in metres, indexed by tracking level 0/1/2. */
export const FLAK_AIM = {
  biasM: AA_DAT_FIELDS.aimBiasUnits.map((units) => units * METRES_PER_UNIT),
  scatterM: AA_DAT_FIELDS.scatterUnits.map((units) => units * METRES_PER_UNIT),
} as const;

/**
 * Seconds one burst spends in each of its four animation frames — the same
 * timer unit everything else in `aa.c` is counted in, because the frame index
 * is `age >> 8` against a counter the shared frame-delta helper drains.
 */
export const FLAK_BURST_FRAME_SECONDS = FLAK_TIMER_UNIT_SECONDS;

/** `AA.DAT` +0x2e in metres: 1.905 / 6.667 / 1.905 / 0.381 m, frame by frame. */
export const FLAK_BURST_RADII_M: readonly number[] = AA_DAT_FIELDS.burstRadiiUnits.map(
  (units) => units * METRES_PER_UNIT,
);

/**
 * What a shell takes off, RECOVERED — see AD-010 §9.
 *
 * `aa.c` calls `damage.c`'s entry (segment `0x1911` offset `0x2da`) as
 * `damage(target, kind, 1024)` and picks the kind by geometry: **5** when the
 * burst is inside half its own effect radius, **4** otherwise. `damage()`
 * scales that magnitude by a per-kind multiplier table at DGROUP `ds:0x1024`
 * — `{10, 15, 10, 15, 50, 100, 0, …}` — through a 22.10 fixed-point multiply
 * (`(a·b) >> 10`), so `1024` in and `50` or `100` out. Those are the raw
 * numbers the aeroplane's own damage accumulator (`obj[+0x7e]`) takes.
 *
 * The scale that accumulator is measured on is `damage.c`'s own: the severity
 * ladder is `(total − 25) >> 4` capped at 7, and severity 7 destroys the
 * aeroplane — so **137 is a wrecked airframe** (AD-010 §7 already reads the
 * 25 off the same ladder for the smoke trigger).
 *
 * `50 / 137` and `100 / 137` are therefore the shell's share of a whole
 * airframe, and that is the number the original books. This port books damage
 * against **one** of five components instead (`hit-allocation.ts` picks which,
 * by aspect), so the same share is divided by the component count: a burst
 * takes the fraction of the airframe the original says it does, out of the one
 * piece of airframe it landed on. There is no free parameter in that — the
 * numerator is `damage.c`'s, the denominators are `damage.c`'s and this port's
 * own component model's.
 *
 * There is one recovered lever this port does *not* pull. For the **player
 * only**, `damage.c` first scales the magnitude by a three-entry table at
 * `ds:0x1032` — `{512, 768, 1024}` over 1024 — indexed by `ds:0x727f`, and
 * returns early doing **nothing at all** when that index is 0. `ds:0x727f` sits
 * on the same 6-byte stride as GUN JAMS ALLOWED (`ds:0x726d`), LIMITED
 * AMMUNITION (`ds:0x7273`) and AIRCRAFT MAY BE DAMAGED (`ds:0x7279`), so it is
 * a Realism Panel entry, and it is the only three-way one that could gate
 * damage: **COMBAT LEVEL**, exactly where AD-010 §6 predicted it would be. At
 * its lowest setting the original's player is *immune* to flak. The port has no
 * COMBAT LEVEL control yet (`flight/tiers.ts` carries FLIGHT MODEL only), so it
 * behaves as the top setting — the hardest of the three — and the scale is
 * recorded here for whoever adds the panel.
 */
export const FLAK_BURST_DAMAGE_UNITS = { outer: 50, inner: 100 } as const;

/** `25 + 7 × 16` — the total `damage.c` accumulates before severity 7 destroys the aeroplane. */
export const AIRFRAME_DESTROY_DAMAGE_UNITS = 137;

/**
 * The Realism Panel's `COMBAT LEVEL` selector — three settings, the manual's
 * own labels (`realism-panel.png`), lowest first. `EASY` is what the capture
 * shows selected at the `NOVICE` preset (AD-010 §9.4 predicted the field would
 * sit on the Realism Panel; this is the label the port's UI lane already
 * shipped for it — `apps/web`'s `mission/realism-settings.ts`).
 */
export const COMBAT_LEVELS = ["EASY", "AVERAGE", "HARD"] as const;
export type CombatLevel = (typeof COMBAT_LEVELS)[number];

/**
 * `ds:0x1032` over 1024, indexed by COMBAT LEVEL; index 0 means the player
 * takes no flak damage at all. Ordered to match `COMBAT_LEVELS`: `EASY` is the
 * original's own lowest setting, where `damage.c` returns before scoring any
 * damage at all (AD-010 §9.4).
 */
export const FLAK_COMBAT_LEVEL_SCALE: readonly number[] = [0, 768 / 1024, 1];

/**
 * `FLAK_COMBAT_LEVEL_SCALE` at `level`, or `1` (the original's top setting,
 * full damage) when `level` is `undefined` — the port's behaviour before this
 * lane wired a COMBAT LEVEL control in, and still the right default for any
 * encounter that does not carry one (a duel, an older fixture): unscaled flak
 * is the *conservative* default, since it can only ever be more dangerous than
 * a player who has actually chosen the immune end of the dial.
 */
export function combatLevelScale(level: CombatLevel | undefined): number {
  if (level === undefined) return 1;
  const index = COMBAT_LEVELS.indexOf(level);
  return FLAK_COMBAT_LEVEL_SCALE[index] ?? 1;
}

/** Five: the airframe components a hit can land on (`hit-allocation.ts` minus the pilot). */
const AIRFRAME_COMPONENTS = HIT_TARGETS.filter(isComponent).length;

function burstDamage(units: number): number {
  return units / AIRFRAME_DESTROY_DAMAGE_UNITS / AIRFRAME_COMPONENTS;
}

export const FLAK_TUNABLES = {
  /** `50 / 137 / 5` — an outer-half burst's share of the airframe, booked to the one component it found. RECOVERED. */
  outerBurstDamage: burstDamage(FLAK_BURST_DAMAGE_UNITS.outer),
  /** `100 / 137 / 5` — the same for a burst inside half the effect radius. RECOVERED. */
  innerBurstDamage: burstDamage(FLAK_BURST_DAMAGE_UNITS.inner),
} as const;

export type FlakTunableKey = keyof typeof FLAK_TUNABLES;

/**
 * Empty, and that is the point: both entries this register carried are now
 * read out of `RB.EXE` rather than guessed. `hitProbability` is gone entirely
 * — it described a die roll that is not a hit roll (AD-010 §9) — and
 * `damagePerBurst` is replaced by the two recovered magnitudes above.
 */
export const FLAK_CALIBRATION_PENDING: readonly FlakTunableKey[] = [];
export interface BurstLethality {
  readonly lethal: boolean;
  /** True when the aeroplane passes inside half the effect radius — `damage.c` kind 5 rather than 4. */
  readonly inner: boolean;
}

const MISSED: BurstLethality = { lethal: false, inner: false };

/**
 * Whether a shell hurts, and how much — `aa.c`'s own test, which is geometry
 * and not a die roll.
 *
 * This is the one place AD-010's first pass was wrong, and the correction is
 * worth stating plainly. `aa_fire` does roll `rand() & 0xff` against a field of
 * the target's `SKILL.DAT` record, but the flag that roll sets is tested in
 * exactly one place: the pre-detonation branch of `aa.c`'s burst tick, which
 * calls an **AI stimulus** entry (segment `0x226a`, a priority-filtered
 * `notify(obj, kind, …)` writing `obj[+0x62]`/`obj[+0x68]`), not the damage
 * entry. The field itself is 200 / 240 / 250 / 256 out of 256 rising with pilot
 * quality — an alertness number, the wrong shape for a hit chance.
 *
 * Damage comes from the *other* branch. Once the fuse has run out the burst
 * lingers through four animation frames, and on every tick of that linger
 * `aa.c` measures the 3D distance from the burst to the aeroplane against
 * `AA.DAT`'s radius for the current frame, then calls `damage.c` with kind 5
 * inside half that radius and kind 4 outside it.
 *
 * The port resolves a burst once, at creation (AD-010 §6 decision 1), so it
 * integrates that pass analytically. The shell is placed `lead + bias` ahead of
 * the aeroplane along its own nose axis, where `lead` is exactly the distance
 * the aeroplane covers during the fuse — so the along-track separation at
 * detonation is `bias`, and it closes at the aeroplane's own speed. Frame `f`
 * is on screen over `[f·T, (f+1)·T)`; the closest the aeroplane gets during
 * that frame is `alongMin`, and the shell tells if `hypot(miss, alongMin)` is
 * inside that frame's radius.
 *
 * The consequence is the manual's, in arithmetic: at tracking level 0 the bias
 * is 152.4 m, which at any speed an aeroplane flies takes longer to cover than
 * the burst's whole 0.86 s life, so **a gun that has just acquired cannot hit
 * at all**. Only a gun that has ranged in — level 2, bias and scatter both
 * 7.62 m — puts its shells where the aeroplane will actually be.
 */
export function burstLethality(missM: number, biasM: number, speedMps: number): BurstLethality {
  const speed = Math.max(0, speedMps);
  let lethal = false;
  for (let frame = 0; frame < FLAK_BURST_RADII_M.length; frame++) {
    const radiusM = FLAK_BURST_RADII_M[frame] ?? 0;
    const fromS = frame * FLAK_BURST_FRAME_SECONDS;
    const toS = (frame + 1) * FLAK_BURST_FRAME_SECONDS;
    // |bias − speed·t| over t ∈ [fromS, toS): zero if the aeroplane arrives
    // during this frame, else the nearer endpoint.
    const nearS = speed <= 0 ? fromS : Math.min(Math.max(biasM / speed, fromS), toS);
    const alongM = Math.abs(biasM - speed * nearS);
    const separationM = Math.hypot(missM, alongM);
    if (separationM <= radiusM / 2) return { lethal: true, inner: true };
    if (separationM <= radiusM) lethal = true;
  }
  return lethal ? { lethal: true, inner: false } : MISSED;
}
